<template>
  <div
    class="carousel"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
  >
    <button @click="onPrevClick" class="prev">
      <Arrow class="arrow" />
    </button>

    <div class="viewport">
      <div class="track" :style="trackStyle">
        <slot />
      </div>
    </div>

    <button @click="onNextClick" class="next">
      <Arrow class="arrow" />
    </button>
  </div>
</template>

<script>
import Arrow from "@/assets/icons/carousel-arrow.svg";

export default {
  name: "Carousel",
  components: { Arrow },

  props: {
    delay: {
      type: Number,
      default: 5000,
    },
    duration: {
      type: Number,
      default: 400,
    },
  },

  data() {
    return {
      currentIndex: 0,
      timer: null,
      isAutoplayEnabled: true,
      isHovered: false,
      isTouched: false,

      touchStartX: 0,
      touchDeltaX: 0,
    };
  },

  computed: {
    slides() {
      return this.$slots.default || [];
    },

    slidesCount() {
      return this.slides.length;
    },

    trackStyle() {
      return {
        transform: `translateX(-${this.currentIndex * 100}%)`,
        transition: `transform ${this.duration}ms ease`,
      };
    },

    isTouchDevice() {
      return "ontouchstart" in window || navigator.maxTouchPoints > 0;
    },
  },

  mounted() {
    if (this.slidesCount > 1) {
      this.startAutoplay();
    }
  },

  beforeUnmount() {
    this.stopAutoplay();
  },

  methods: {
    onTouchStart(e) {
      if (!this.isTouchDevice) return;

      this.isTouched = true;

      this.touchStartX = e.touches[0].clientX;
      this.touchDeltaX = 0;

      this.disableAutoplayForever();
    },

    onTouchMove(e) {
      if (!this.isTouchDevice) return;

      const currentX = e.touches[0].clientX;
      this.touchDeltaX = currentX - this.touchStartX;
    },

    onTouchEnd() {
      if (!this.isTouchDevice) return;

      const SWIPE_THRESHOLD = 50;

      if (Math.abs(this.touchDeltaX) < SWIPE_THRESHOLD) return;

      if (this.touchDeltaX < 0) {
        this.next();
      } else {
        this.prev();
      }

      this.touchDeltaX = 0;
    },
    startAutoplay() {
      if (!this.isAutoplayEnabled || this.timer) return;

      this.timer = setInterval(() => {
        this.next();
      }, this.delay);
    },

    stopAutoplay() {
      clearInterval(this.timer);
      this.timer = null;
    },

    pauseAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    next() {
      this.currentIndex = (this.currentIndex + 1) % this.slidesCount;
    },

    prev() {
      this.currentIndex =
        (this.currentIndex - 1 + this.slidesCount) % this.slidesCount;
    },

    onMouseEnter() {
      if (this.isTouchDevice) return;
      this.isHovered = true;
      this.pauseAutoplay();
    },

    onMouseLeave() {
      if (this.isTouchDevice) return;
      this.isHovered = false;

      if (this.isAutoplayEnabled) {
        this.startAutoplay();
      }
    },

    onPrevClick() {
      this.prev();
      this.handleUserInteraction();
    },

    onNextClick() {
      this.next();
      this.handleUserInteraction();
    },

    handleUserInteraction() {
      if (this.isTouchDevice) {
        this.disableAutoplayForever();
      } else {
        this.disableAutoplayForever();
      }
    },

    disableAutoplayForever() {
      this.isAutoplayEnabled = false;
      this.stopAutoplay();
    },
  },
};
</script>

<style scoped lang="less">
.carousel {
  display: flex;
  align-items: center;
  background-color: #fff;
}

.viewport {
  overflow: hidden;
  width: 100%;
}

.track {
  display: flex;
  width: 100%;
  touch-action: pan-y;
}

.track > * {
  flex: 0 0 100%;
}

.prev,
.next {
  background-color: #fff;
  cursor: pointer;
  width: 40px;
  min-width: 40px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 767px) {
    min-width: 24px;
    width: 24px;
    height: 48px;
  }
  .arrow {
    width: 24px;
    height: 43px;
    @media (max-width: 767px) {
      width: 14px;
      height: 26px;
    }
  }
}
.next .arrow {
  transform: rotate(180deg);
}
</style>
